import React, { useMemo } from 'react';
import type { CalendarWeekDay } from '../../../../hooks/useCalendarWeek';
import type {
    CalendarAppointmentDto,
    CalendarStatusDto,
    CalendarStatusKey,
} from '../../../../types/api-responses';
import { AppointmentStatus } from '../../../../types/enums';
// AB#1901 (W-5) — Boundary-parse appointment.status to handle both BE wire
// strings ("Pending"/"Completed"/…) and legacy int form. Without this the
// switch fell through default for every string value → every appointment
// rendered with the Booked colour stripe regardless of actual status.
import { parseAppointmentStatus } from '../../../../lib/enumParsers';
import { useLanguage } from '../../../../contexts/LanguageContext';
import s from './WeekView.module.css';

interface WeekViewProps {
    days: CalendarWeekDay[];
    /** "yyyy-MM-dd" of today, for the "today" column highlight. */
    todayIso: string;
    /**
     * Click-to-create — when an empty (day, hour) cell is clicked, the page
     * opens the booking modal pre-filled with the first staff at the branch
     * + the clicked hour. Week view doesn't render per-staff columns so it
     * has to pick a default staff; the page resolves which one. The cell
     * passes `(date, "HH:00")` and the page injects staff context.
     */
    onEmptyCellClick?: (date: string, timeHHmm: string) => void;
}

/**
 * AB#1749 — Compact week glance (mockup §B).
 *
 * 8-col grid: time-axis (60px) + 7 days (1fr each). Rows are one hour
 * each, spanning the union of all 7 days' branch hours. Each appointment
 * is rendered as a compact strip inside its day-column / hour-row cell,
 * positioned by `top = minutesPastHour * px-per-min`.
 *
 * Card content is intentionally tight per mockup: "Customer · Service"
 * single-line strip — the full card detail lives on the Day view.
 *
 * Closed days (BranchHours.IsOpen=false for that day) and days that
 * haven't loaded yet show a faint hatch overlay so the column still
 * reads as "there's a day here, just no data".
 */
const WeekView: React.FC<WeekViewProps> = ({ days, todayIso, onEmptyCellClick }) => {
    // Compute the union of dayStart/dayEnd across all loaded days so the
    // time-axis covers every appointment in the visible week. Returns
    // the list of hour-rows (e.g. [9,10,11,...,17]).
    const rows = useMemo(() => {
        let minStart = Infinity;
        let maxEnd = -Infinity;
        for (const d of days) {
            if (!d.grid || d.grid.isClosed) continue;
            const s2 = parseHHmm(d.grid.dayStart);
            const e2 = parseHHmm(d.grid.dayEnd);
            if (s2 < minStart) minStart = s2;
            if (e2 > maxEnd) maxEnd = e2;
        }
        // Fallback when every day is loading / closed.
        if (!Number.isFinite(minStart) || !Number.isFinite(maxEnd)) {
            minStart = 9 * 60;
            maxEnd = 18 * 60;
        }
        const sH = Math.floor(minStart / 60);
        const eH = Math.ceil(maxEnd / 60);
        const rs: number[] = [];
        for (let h = sH; h <= eH - 1; h++) rs.push(h);
        return rs;
    }, [days]);

    const PIXELS_PER_MINUTE = 0.9; // 54px / hour — tight strip

    return (
        <div className={s.weekShell}>
            {/* Header — empty corner + 7 day cells with weekday + day-num */}
            <div className={s.weekHead}>
                <div className={s.headCorner} />
                {days.map((d) => {
                    const dayObj = new Date(`${d.date}T00:00:00`);
                    const weekday = dayObj.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase();
                    const dayNum = dayObj.getDate();
                    const isToday = d.date === todayIso;
                    return (
                        <div key={d.date} className={s.dayHead}>
                            <div className={s.dayName}>{weekday}</div>
                            <div className={`${s.dayNum} ${isToday ? s.dayNumToday : ''}`}>{dayNum}</div>
                        </div>
                    );
                })}
            </div>

            {/* Body — 1-hour rows */}
            <div className={s.weekBody}>
                {rows.map((h) => (
                    <div key={h} className={s.weekRow}>
                        <div className={s.timeLabel}>{String(h).padStart(2, '0')}:00</div>
                        {days.map((d) => (
                            <DayCell
                                key={d.date}
                                day={d}
                                hour={h}
                                pixelsPerMinute={PIXELS_PER_MINUTE}
                                onEmptyCellClick={onEmptyCellClick}
                            />
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default WeekView;

/* ──────────────────────── helpers ──────────────────────── */

function parseHHmm(t?: string | null): number {
    if (!t || !t.includes(':')) return 0;
    const [h, m] = t.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return 0;
    return h * 60 + m;
}

function statusToKey(status: number | string): CalendarStatusKey {
    // AB#1901 (W-5) — boundary-parse the wire shape before mapping.
    const normalized = parseAppointmentStatus(status);
    switch (normalized) {
        case AppointmentStatus.Pending: return 'Booked';
        case AppointmentStatus.Confirmed: return 'Booked';   // BE "Approved" → FE Confirmed → Booked
        case AppointmentStatus.Completed: return 'Completed';
        case AppointmentStatus.Cancelled: return 'Cancelled';
        case AppointmentStatus.NoShow: return 'NoShow';
        case 6: return 'Arrived';
        case 7: return 'Started';
        default:
            if (typeof console !== 'undefined' && console.warn) {
                console.warn('[WeekView] Unknown appointment status:', status);
            }
            return 'Booked';
    }
}

function hexToRgba(hex: string, alpha: number): string {
    const m = /^#([0-9a-f]{6})$/i.exec(hex);
    if (!m) return `rgba(120, 120, 130, ${alpha})`;
    const n = parseInt(m[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

interface DayCellProps {
    day: CalendarWeekDay;
    hour: number;
    pixelsPerMinute: number;
    onEmptyCellClick?: (date: string, timeHHmm: string) => void;
}

/**
 * One cell = one (day, hour) intersection. Renders every appointment
 * whose start time falls in [hour:00, hour+1:00). Cards are positioned
 * by `top = (startMin - hour*60) * pxPerMin`. Cards spanning two hours
 * still anchor in their starting hour-cell — visually OK for a "glance"
 * view; the Day view is where exact spans matter.
 */
const DayCell: React.FC<DayCellProps> = ({ day, hour, pixelsPerMinute, onEmptyCellClick }) => {
    const { t } = useLanguage();
    const isClosed = !day.grid || day.grid.isClosed;
    const statuses: CalendarStatusDto[] = day.grid?.statuses ?? [];

    const apptsInThisHour = useMemo(() => {
        if (!day.grid) return [] as CalendarAppointmentDto[];
        return day.grid.appointments.filter((a) => {
            const startMin = parseHHmm(a.startTime);
            return Math.floor(startMin / 60) === hour;
        });
    }, [day.grid, hour]);

    // Click-to-create — skip the binding on cells whose underlying day is
    // closed for this branch. Pre-2026-05-24 the BE collapsed every
    // rejection into Slot.CONFLICT so we bound the click everywhere and
    // hoped the owner would understand the "slot was just taken" toast
    // really meant "branch is closed". Now that BE returns the distinct
    // Slot.BRANCH_CLOSED code, the cleaner UX is to suppress the
    // affordance entirely on hatched-overlay (closed) cells — the owner
    // shouldn't get a hover hint for a day the booking can't succeed on.
    const clickable = !!onEmptyCellClick && !isClosed;
    const onClick = clickable
        ? (e: React.MouseEvent<HTMLDivElement>) => {
            // Bail if the click landed on a child appointment strip — let
            // those handle their own behaviour (currently read-only tooltip).
            // We previously used `target.closest('.${s.weekCell}') !==
            // e.currentTarget` which silently broke when the CSS-module hash
            // contained a `+` (a CSS adjacent-sibling combinator), producing
            // an invalid selector → `closest()` returned null → every click
            // was suppressed. Targeted check on the appointment class is
            // both narrower and immune to hash-character collisions; the
            // CSS.escape() wrapper keeps it future-proof if either class
            // ever gets a `:`, `.`, or other special character in its hash.
            const target = e.target as HTMLElement;
            if (target.closest(`.${CSS.escape(s.weekAppt)}`)) return;
            const timeHHmm = `${String(hour).padStart(2, '0')}:00`;
            onEmptyCellClick!(day.date, timeHHmm);
        }
        : undefined;

    // Layer 1 (audit Phase B-1) — density rendering.
    //
    // Pre-fix: every appointment in (day, hour) rendered full-width, stacked
    // with positioning. 5 bookings at 10:00 = 5 overlapping <div>s, only the
    // last-painted visible — the other 4 silently invisible.
    //
    // Now: split the cell horizontally.
    //   N = 1            → full width, current behaviour
    //   N = 2 or 3       → side-by-side at 1/N width (still legible since
    //                      Week-view cells average ~120-160px and the cards
    //                      only show first-name + start-time)
    //   N >= 4           → render first 2 side-by-side + a "+(N-2) more"
    //                      chip; chip opens an inline popover listing
    //                      every booking in this cell with status + time +
    //                      customer + service. Popover has a "Book new at
    //                      this time" CTA so the cell click affordance
    //                      isn't lost behind the overflow stack.
    //
    // Click semantics:
    //   - Click on a card        → existing tooltip behaviour (no-op)
    //   - Click on +N more       → opens popover; doesn't bubble to cell
    //   - Click on cell empty    → opens QuickBookModal (existing onClick)
    //   - Click in popover Book  → closes popover + fires onEmptyCellClick
    const N = apptsInThisHour.length;
    const visibleCount = N <= 3 ? N : 2;
    const overflowCount = N - visibleCount;
    const slotsUsed = visibleCount + (overflowCount > 0 ? 1 : 0);
    const slotPct = slotsUsed > 0 ? 100 / slotsUsed : 100;
    const visibleAppts = apptsInThisHour.slice(0, visibleCount);

    const [popoverOpen, setPopoverOpen] = React.useState(false);
    // Close popover on Escape so keyboard users aren't trapped.
    React.useEffect(() => {
        if (!popoverOpen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPopoverOpen(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [popoverOpen]);

    return (
        <div
            className={`${s.weekCell} ${isClosed ? s.weekCellClosed : ''} ${clickable ? s.weekCellClickable : ''}`}
            onClick={onClick}
            title={clickable ? `Click to book at ${String(hour).padStart(2, '0')}:00` : undefined}
        >
            {visibleAppts.map((a, idx) => {
                const startMin = parseHHmm(a.startTime);
                const endMin = parseHHmm(a.endTime);
                const topPx = Math.max(0, (startMin - hour * 60) * pixelsPerMinute);
                const heightPx = Math.max(22, Math.min(54 - topPx, (endMin - startMin) * pixelsPerMinute));
                const key = statusToKey(a.status);
                const row = statuses.find((st) => st.statusKey === key) ?? statuses[0];
                const bg = row ? hexToRgba(row.colorHex, 0.18) : 'var(--primary-subtle)';
                const border = row?.colorHex ?? 'var(--primary)';
                const isCancelled = key === 'Cancelled';
                const customer = a.customerName ?? 'Walk-in';
                // AB#2164 — Pending and Approved share the Booked row; mark
                // Pending strips so owners see what still awaits confirmation.
                const awaitingLabel = parseAppointmentStatus(a.status) === AppointmentStatus.Pending
                    ? t('calendar.card.awaitingConfirmation')
                    : null;
                return (
                    <div
                        key={a.id}
                        className={`${s.weekAppt} ${isCancelled ? s.weekApptCancelled : ''}`}
                        style={{
                            top: `${topPx}px`,
                            height: `${heightPx}px`,
                            insetInlineStart: `${idx * slotPct}%`,
                            width: `${slotPct}%`,
                            background: bg,
                            borderInlineStartColor: border,
                        }}
                        title={`${row?.labelEn ?? 'Booked'}${awaitingLabel ? ` · ${awaitingLabel}` : ''} · ${a.startTime}–${a.endTime} · ${customer} · ${a.serviceName}`}
                    >
                        {awaitingLabel && (
                            <span className={s.awaitingBadge} role="img" aria-label={awaitingLabel}>⏳</span>
                        )}
                        {customer} · {a.serviceName}
                    </div>
                );
            })}
            {overflowCount > 0 && (
                <>
                    <button
                        type="button"
                        className={s.weekApptOverflow}
                        style={{
                            insetInlineStart: `${visibleCount * slotPct}%`,
                            width: `${slotPct}%`,
                        }}
                        onClick={(e) => { e.stopPropagation(); setPopoverOpen(true); }}
                        title={`Show all ${N} bookings at this time`}
                    >
                        +{overflowCount} more
                    </button>
                    {popoverOpen && (
                        <div
                            className={s.weekOverflowPopover}
                            role="dialog"
                            aria-label={`All ${N} bookings at ${String(hour).padStart(2, '0')}:00 on ${day.date}`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className={s.weekOverflowPopoverHead}>
                                <strong>{String(hour).padStart(2, '0')}:00 · {N} {N === 1 ? 'booking' : 'bookings'}</strong>
                                <button
                                    type="button"
                                    className={s.weekOverflowPopoverClose}
                                    onClick={(e) => { e.stopPropagation(); setPopoverOpen(false); }}
                                    aria-label="Close"
                                >×</button>
                            </div>
                            <ul className={s.weekOverflowPopoverList}>
                                {apptsInThisHour.map(a => {
                                    const key = statusToKey(a.status);
                                    const row = statuses.find((st) => st.statusKey === key) ?? statuses[0];
                                    const dot = row?.colorHex ?? 'var(--primary)';
                                    const customer = a.customerName ?? 'Walk-in';
                                    return (
                                        <li key={a.id} className={s.weekOverflowPopoverItem}>
                                            <span className={s.weekOverflowPopoverDot} style={{ background: dot }} />
                                            <span className={s.weekOverflowPopoverTime}><bdi dir="ltr">{a.startTime}–{a.endTime}</bdi></span>
                                            <span className={s.weekOverflowPopoverCustomer}>{customer}</span>
                                            <span className={s.weekOverflowPopoverService}>{a.serviceName}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                            {clickable && onEmptyCellClick && (
                                <button
                                    type="button"
                                    className={s.weekOverflowPopoverCta}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setPopoverOpen(false);
                                        const timeHHmm = `${String(hour).padStart(2, '0')}:00`;
                                        onEmptyCellClick(day.date, timeHHmm);
                                    }}
                                >
                                    + Book new at {String(hour).padStart(2, '0')}:00
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
